import { apiFetch } from './http';
import { clearToken, clearStoredSession } from './sessionStorage';

/**
 * Auth API — LIVE against PocketBase (auth-full-spec.md §0–§4).
 *
 * All flows run same-origin through the host Caddy /api/* proxy (PocketBase on
 * 127.0.0.1:8527). The Node backend owns /api/clients* (the public register
 * endpoint in routes/clients.js); PB owns the auth endpoints (/api/collections/
 * users/*) and /api/oauth2-redirect.
 *
 * Every flow returns/derives the session through sessionFromPbAuth — claims are
 * the ONLY identity source (role from record.role, tenant from record.client_id).
 */

const PB_API = '/api';

/**
 * sessionStorage key for the in-flight OAuth2 dance (auth-full-spec §4):
 * { provider, state, codeVerifier } — persisted so the callback page can
 * validate ?state and complete the PKCE exchange after the redirect chain.
 */
const OAUTH_PENDING_KEY = 'agiOps:oauth.pending';

// Reasonable cap on the pending record so a corrupt/garbage entry cannot grow
// unbounded; anything unreadable is treated as absent.
function readOauthPending() {
  try {
    const raw = window.sessionStorage.getItem(OAUTH_PENDING_KEY);
    if (!raw || raw.length > 4096) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.provider && parsed.state && parsed.codeVerifier ? parsed : null;
  } catch {
    return null;
  }
}

function writeOauthPending(value) {
  try {
    if (value) window.sessionStorage.setItem(OAUTH_PENDING_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(OAUTH_PENDING_KEY);
  } catch {
    // sessionStorage unavailable — the dance cannot carry PKCE state; treated
    // as absent by the callback page (fail-closed, no exchange).
  }
}

function pbError(data, fallback) {
  const error = new Error(data?.message || fallback);
  error.code = data?.code || 'pb_error';
  error.status = data?.status || 400;
  return error;
}

async function pbFetch(path, { method = 'GET', body } = {}) {
  const response = await fetch(`${PB_API}${path}`, {
    method,
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw pbError(data, 'Request failed.');
  return data;
}

// PB role vocabulary → frontend vocabulary (docs/planned_work/auth-permissions.md;
// go-live-readiness.md §3). Claims are the only source — the old email-regex
// tenant/role guessing is gone.
//
// Unprovisioned gate (auth-full-spec §4, user decision LOCKED): a missing or
// unrecognized role means the Google account is NOT provisioned — sessionFromPbAuth
// maps it to role 'unprovisioned'; the SSO callback page refuses to apply such a
// session and routes the person to the request-only register flow instead.
// backend/middleware/auth.js mirrors this: missing/unknown role now REJECTS (401),
// never defaults to a viewer role.
const ROLE_BY_PB_ROLE = {
  agency_admin: 'agency-admin',
  agencyadmin: 'agency-admin',
  'agency-admin': 'agency-admin',
  client: 'client',
  operator: 'operator'
};

function normalizeRole(rawRole) {
  const key = String(rawRole ?? '').trim().toLowerCase();
  return ROLE_BY_PB_ROLE[key] || 'unprovisioned';
}

function sessionFromPbAuth(data) {
  const record = data.record || {};
  const email = record.email || '';
  // Tenant identity comes from the client_id claim on the PB record — the
  // same claim backend/middleware/auth.js derives tenantId from.
  const tenantId = record.client_id || null;
  const role = normalizeRole(record.role);
  return {
    user: { id: record.id, name: record.name || email.split('@')[0], email },
    role,
    tenant: tenantId || (role === 'unprovisioned' ? null : 'agency'),
    tenantId
  };
}

/**
 * OTP login (PB auth-with-otp, v0.39 wire contract — verified live):
 *   step 1  POST /api/collections/users/request-otp {email}   → {otpId}
 *   step 2  POST /api/collections/users/auth-with-otp {otpId, password}
 * where `password` is the 8-digit code from the email. otpId is single-use and
 * expires with the code. PB returns 204-with-empty-body semantics per request —
 * an unknown email is indistinguishable from a known one (enumeration-safe);
 * the caller surfaces a generic failure only for transport/5xx errors.
 */
export async function requestOtp({ email }) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized) throw new Error('Enter your email address.');
  try {
    const data = await pbFetch('/collections/users/request-otp', {
      method: 'POST',
      body: { email: normalized }
    });
    return { ok: true, email: normalized, otpId: data.otpId };
  } catch {
    // Only network/5xx land here (PB answers 400/204 for well-formed requests).
    const error = new Error("Couldn't send code — try again.");
    error.code = 'otp_send_failed';
    throw error;
  }
}

export async function verifyOtp({ otpId, otp }) {
  if (!otpId) throw new Error('Request a new code (session expired).');
  const code = String(otp || '').trim();
  if (!code) throw new Error('Enter the code from your email.');
  try {
    const data = await pbFetch('/collections/users/auth-with-otp', {
      method: 'POST',
      body: { otpId, password: code }
    });
    return { token: data.token, session: sessionFromPbAuth(data) };
  } catch (error) {
    // Account-enumeration hygiene: same message for unknown email vs bad code.
    error.message = 'Invalid or expired code.';
    throw error;
  }
}

/**
 * Password login (LIVE — PB auth-with-password, v0.39 wire contract). Identity
 * is the email; PB returns {token, record} and sessionFromPbAuth maps claims
 * (role from record.role, tenant from record.client_id) exactly like the OTP
 * flow.
 */
export async function loginRequest({ email, password }) {
  const identity = String(email || '').trim().toLowerCase();
  if (!identity || !password) throw new Error('Enter your email and password.');
  try {
    const data = await pbFetch('/collections/users/auth-with-password', {
      method: 'POST',
      body: { identity, password: String(password) }
    });
    return { token: data.token, session: sessionFromPbAuth(data) };
  } catch {
    // Account-enumeration hygiene: one message for unknown email, wrong
    // password, and PB transport errors alike.
    const error = new Error('Invalid email or password.');
    error.code = 'invalid_credentials';
    throw error;
  }
}

/**
 * Register — REQUEST-ONLY by design (auth-full-spec §1): this NEVER creates a
 * PocketBase user. It files a client_requests row through the Node backend's
 * UNAUTHENTICATED public endpoint so the request is visible in the agency
 * triage feed (GET /api/clients/requests). The agency provisions the account
 * out of band (§7). No session, no token — nothing is returned to apply.
 */
export async function registerRequest({ email, password, name, tenant }) {
  // password is accepted for form symmetry but deliberately unused: a public
  // request must not carry a credential.
  void password;
  const normalizedEmail = String(email || '').trim().toLowerCase();
  if (!normalizedEmail) throw new Error('Enter your email address.');
  try {
    await apiFetch('/api/clients/public-requests', {
      method: 'POST',
      body: JSON.stringify({
        name: String(name || '').trim().slice(0, 80),
        email: normalizedEmail,
        organization: String(tenant || '').trim().slice(0, 120)
      })
    });
    return { ok: true };
  } catch {
    // Same response shape regardless of PB state — the register form must not
    // leak whether an account/tenant already exists.
    const error = new Error('Unable to submit your request right now — please try again.');
    error.code = 'register_request_failed';
    throw error;
  }
}

/**
 * Forgot password — LIVE PB request-password-reset (auth-full-spec §2).
 * Enumeration-safe: PB answers the same for known/unknown emails, and this
 * wrapper always resolves to the same success copy.
 */
export async function forgotPasswordRequest({ email }) {
  const normalized = String(email || '').trim().toLowerCase();
  if (!normalized) throw new Error('Enter your email address.');
  try {
    await pbFetch('/collections/users/request-password-reset', {
      method: 'POST',
      body: { email: normalized }
    });
  } catch {
    // Swallow: PB returns 400 for unknown emails (rate limits aside) — the
    // copy below is what the user sees either way (enumeration-safe).
  }
  return { ok: true, message: `If ${email} matches an account, a reset link is on its way.` };
}

/**
 * Reset password — LIVE PB confirm-password-reset (auth-full-spec §2). PB's
 * contract requires token + password + passwordConfirm; expired/used tokens
 * reject with 400 and surface as an invalid-link error (never silently OK).
 */
export async function resetPasswordRequest({ token, password, passwordConfirm }) {
  if (!token) {
    const error = new Error('This reset link is invalid or has expired.');
    error.code = 'invalid_token';
    throw error;
  }
  if (!passwordConfirm) throw new Error('Confirm the new password.');
  try {
    await pbFetch('/collections/users/confirm-password-reset', {
      method: 'POST',
      body: { token, password: String(password), passwordConfirm: String(passwordConfirm) }
    });
    return { ok: true };
  } catch (error) {
    if (error.code === 'validation_invalid_password') {
      const message = new Error('Choose a stronger password — 8+ characters with a number or symbol.');
      message.code = 'weak_password';
      throw message;
    }
    if (error.status === 400) {
      const invalid = new Error('This reset link is invalid or has expired. Request a new one.');
      invalid.code = 'invalid_token';
      throw invalid;
    }
    throw error;
  }
}

/**
 * Google OAuth2 — real hand-rolled PKCE dance (auth-full-spec §4; the
 * pocketbase JS SDK is deliberately NOT used — zero new deps).
 *
 * RESOLVED WIRING (verified live 2026-09-21 + PB source):
 * 1. GET /api/collections/users/auth-methods → per provider {name, state,
 *     codeVerifier, authURL}. authURL points at Google with PKCE params and
 *     `redirect_uri=` EMPTY — the CLIENT appends its own redirect (memory
 *     pb-google-oauth2-config-20260921: empty redirect_uri is by design).
 *     We append redirect_uri=<origin>/oauth2/callback.html — a plain PATH
 *     (no fragment): OAuth2 forbids fragments in redirect URIs (RFC 6749
 *     §3.1.2), so the hash router cannot receive the code directly. The
 *     static shim at public/oauth2/callback.html forwards ?code&state into
 *     /#/sso/<provider> and the dance completes there.
 *  2. Google consent → 302 to /oauth2/callback.html?code=...&state=...
 *     → shim replaces to /#/sso/google?code=...&state=...
 *  3. SSOCallbackPage: state must match the pending record (CSRF), then
 *     POST /api/collections/users/auth-with-oauth2 {provider, code,
 *     codeVerifier, redirectURL} — PB exchanges the code with Google
 *     (redirect_uri must equal the one authURL carried) and returns
 *     {token, record}. PB never redirects anywhere itself; /api/oauth2-redirect
 *     exists only for the SDK's popup+realtime flow, which we don't use.
 *  4. Session applies only when role is provisioned — unprovisioned gate in
 *     SSOCallbackPage.
 */
export async function startOauth2(providerName) {
  const name = String(providerName || '').trim();
  if (!name) throw new Error('Choose a sign-in provider.');
  const methods = await pbFetch('/collections/users/auth-methods');
  const provider = (methods?.oauth2?.providers || []).find((entry) => entry.name === name);
  if (!provider || !provider.authURL || !provider.state || !provider.codeVerifier) {
    const error = new Error('Google sign-in is not available right now.');
    error.code = 'oauth_provider_missing';
    throw error;
  }
  writeOauthPending({
    provider: provider.name,
    state: provider.state,
    codeVerifier: provider.codeVerifier
  });
  // RFC 6749 §3.1.2: no fragments in redirect URIs — exact-file path, served
  // as-is by Caddy (try_files {path} hits the real file). The same string is
  // reused in exchangeOauth2Code because PB validates the code exchange
  // redirect_uri for exact string equality against the consent request.
  const redirectUri = `${window.location.origin}/oauth2/callback.html`;
  const joiner = provider.authURL.includes('?') ? '&' : '?';
  // No double-encoding: authURL already carries url-encoded params; we only
  // append the raw redirect_uri (URL.fromString in PB decodes it once).
  window.location.assign(`${provider.authURL}${joiner}redirect_uri=${redirectUri}`);
}

/** Step 3 of the dance — exchange ?code for a PB session (called from SSOCallbackPage). */
export async function exchangeOauth2Code({ provider, code, state }) {
  const pending = readOauthPending();
  writeOauthPending(null);
  if (!pending) {
    const error = new Error('Sign-in session expired — start the Google sign-in again.');
    error.code = 'oauth_state_missing';
    throw error;
  }
  // CSRF: the state Google echoes must be the one bound to this browser's
  // pending dance (and to the same provider).
  if (pending.provider !== provider || pending.state !== state) {
    const error = new Error('Sign-in could not be verified — start the Google sign-in again.');
    error.code = 'oauth_state_mismatch';
    throw error;
  }
  const data = await pbFetch('/collections/users/auth-with-oauth2', {
    method: 'POST',
    body: {
      provider: pending.provider,
      code: String(code || ''),
      codeVerifier: pending.codeVerifier,
      // Must equal the redirect_uri the consent request carried (exact string
      // match — see startOauth2).
      redirectURL: `${window.location.origin}/oauth2/callback.html`
    }
  });
  return { token: data.token, session: sessionFromPbAuth(data) };
}

/**
 * Logout — PB tokens are stateless JWTs (no server session to revoke via a
 * public endpoint), so logout is a pure client-side clear: token + session
 * storage dropped, store reset by the caller (sessionSlice.clearSession).
 */
export function logoutRequest() {
  clearToken();
  clearStoredSession();
  return Promise.resolve({ ok: true });
}
